import type { ReactNode } from 'react'
import { useOmniAuth, useOmniSession } from '@omni/auth'
import { LogOut, RefreshCw } from 'lucide-react'
import { activeInstance } from '../instances'
import { productName } from '../lib/config'
import { ASK_FOR_ACCESS, FRONT_PAGE_URL } from './config'
import './front-door.css'

// The whole-window screens of the front door (doc section 11): not on the
// list, no access to this app, and the access check failing. Branded like
// the sign-in screen; no other tabs.

const theme = activeInstance.theme
const entry = activeInstance.entry ?? {}
const GROUND = entry.background ?? theme.gradient ?? 'linear-gradient(160deg, #0b0c18 0%, #15162b 100%)'
const LOGO = entry.logoUrl ?? theme.wordmarkUrl ?? theme.logoUrl

function Shell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="fd-screen" style={{ background: GROUND }}>
      <div className="fd-screen-in">
        {LOGO ? <img src={LOGO} alt={productName} className="fd-screen-logo" /> : <p className="fd-screen-name">{productName}</p>}
        <div className="fd-screen-card" role="alert">
          <h1>{title}</h1>
          {children}
        </div>
      </div>
    </div>
  )
}

function SignOut() {
  const { signOut } = useOmniAuth()
  return (
    <button type="button" className="fd-btn fd-btn-quiet" onClick={() => void signOut()}>
      <LogOut size={15} /> Sign out
    </button>
  )
}

/** Signed in, but this account is not on the list (or was removed). */
export function NotOnList() {
  const { primaryEmail } = useOmniSession()
  return (
    <Shell title={`This account does not have access to ${productName}.`}>
      <p>
        {primaryEmail ? (
          <>
            You are signed in as <strong>{primaryEmail}</strong>.{' '}
          </>
        ) : null}
        Ask {ASK_FOR_ACCESS} to add you, or sign out and use your work account.
      </p>
      <div className="fd-screen-actions">
        <SignOut />
      </div>
    </Shell>
  )
}

/** On the list, but not granted this app. */
export function NoAccess({ app, frontPageUrl = FRONT_PAGE_URL }: { app: string; frontPageUrl?: string }) {
  return (
    <Shell title={`You do not have access to ${app}.`}>
      <p>Your other apps are on the front page. Ask {ASK_FOR_ACCESS} if you need this one.</p>
      <div className="fd-screen-actions">
        <a className="fd-btn" href={frontPageUrl}>
          Go to the front page
        </a>
        <SignOut />
      </div>
    </Shell>
  )
}

/** The access check did not answer. */
export function AccessError({ app, retry }: { app: string; retry: () => void }) {
  return (
    <Shell title={`We could not check your access to ${app} just now.`}>
      <p>Your connection or the database did not answer. Try again in a moment.</p>
      <div className="fd-screen-actions">
        <button type="button" className="fd-btn" onClick={retry}>
          <RefreshCw size={15} /> Try again
        </button>
        <a className="fd-btn fd-btn-quiet" href={FRONT_PAGE_URL}>
          Go to the front page
        </a>
      </div>
    </Shell>
  )
}
